import { Button } from "@/components/ui/button";
import { FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { HoverCard, HoverCardContent } from "@/components/ui/hover-card";
import { Input } from "@/components/ui/input";
import { InformationCircleIcon } from "@heroicons/react/24/outline";
import { HoverCardTrigger } from "@radix-ui/react-hover-card";
import { JSX, Ref } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";

interface InputFieldProps extends FormFieldComponentProps {
  ref?: Ref<HTMLInputElement>;
  /** Optional callback called when the field loses focus */
  onBlur?: () => void;
}

const InputField = (props: InputFieldProps): JSX.Element => {
  const {
    form,
    name,
    label,
    type = "text",
    placeholder,
    disabled,
    defaultValue,
    className,
    tabIndex,
    onKeyDown,
    autoFocus,
    description,
    showOptional,
    showInfo,
    infoTitle,
    infoText,
    ref,
    onBlur: customOnBlur,
  } = props;

  const { t } = useTranslation(["common"]);

  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => (
        <FormItem className={cn(className ?? "form-item")}>
          {label &&
            <FormLabel className="flex items-center">{label}
              {
                showInfo && (
                  <HoverCard>
                    <HoverCardTrigger>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-5 w-5 p-0 hover:cursor-help"
                        type="button"
                      >
                        <InformationCircleIcon className="h-5 w-5 text-slate-500" />
                      </Button>
                    </HoverCardTrigger>
                    <HoverCardContent >
                      <h2 className="text-sm mb-2">{infoTitle}</h2>
                      <p className="text-sm font-normal text-slate-400">{infoText}</p>
                    </HoverCardContent>
                  </HoverCard>
                )
              }
              {showOptional &&
                <span className="text-slate-400 font-normal">&nbsp;({t("optional")})</span>
              }
            </FormLabel>
          }
          <FormControl>
            <div className="flex items-center gap-2">
              <Input
                {...field}
                type={type}
                disabled={disabled}
                placeholder={placeholder}
                defaultValue={defaultValue}
                tabIndex={tabIndex}
                onKeyDown={onKeyDown}
                autoFocus={autoFocus}
                ref={ref}
                onBlur={(_e) => {
                  field.onBlur();
                  customOnBlur?.();
                }}
              />
            </div>
          </FormControl>
          {description && <FormDescription>{description}</FormDescription>}
          <FormMessage />
        </FormItem>
      )}
    />
  );
};

export default InputField;
